import { useEffect } from 'react'
import { useProfileModeStore } from '@/stores/useProfileModeStore'
import { useSupabase } from '@/hooks/useSupabase'

/**
 * 프로필 모드 관리 Hook (Zustand Store 기반)
 * 
 * 활성 프로필은 DB의 is_active 상태를 기준으로 관리합니다.
 * - GET /api/profile/active로 활성 프로필 조회 (캐싱 및 중복 호출 방지)
 * - POST /api/profile/switch로 프로필 전환
 * - 스키마: accounts = 프리랜서 프로필 (user당 1개), client = 기업 프로필 (user당 1개)
 * 
 * @returns {Object} 모드 관련 상태 및 함수
 * - mode: 현재 모드 ('FREELANCER' | 'COMPANY' | null)
 * - isFreelancer: 프리랜서 모드 여부
 * - isCompany: 기업 모드 여부
 * - hasFreelancer: 프리랜서 프로필 존재 여부
 * - hasCompany: 기업 프로필 존재 여부
 * - switchMode: 모드 전환 함수 (DB 업데이트)
 * - loading: 로딩 상태
 */
export const useProfileMode = (): {
  mode: 'FREELANCER' | 'COMPANY' | null
  isFreelancer: boolean
  isCompany: boolean
  hasFreelancer: boolean
  hasCompany: boolean
  switchMode: (next: 'FREELANCER' | 'COMPANY' | 'NONE') => Promise<void>
  loading: boolean
} => {
  const store = useProfileModeStore()
  const { user, loading: authLoading } = useSupabase()

  // 로그인된 사용자만 프로필 모드 조회
  useEffect(() => {
    // 클라이언트에서만 실행
    if (typeof window === 'undefined') return
    
    // 인증 로딩 중이면 대기
    if (authLoading) return

    // 비로그인 상태면 프로필 모드 초기화하고 API 호출하지 않음
    if (!user) {
      store.reset()
      return
    }

    // 로그인된 사용자만 초기 fetch (store 내부에서 캐싱 및 중복 호출 방지 처리)
    const fetchActive = store.fetchActive
    fetchActive()

    // profileSwitched 이벤트 리스너 등록
    const handleProfileSwitch = () => {
      // 로그인 상태일 때만 fetch
      if (user) {
        fetchActive()
      }
    }

    window.addEventListener('profileSwitched', handleProfileSwitch)

    return () => {
      window.removeEventListener('profileSwitched', handleProfileSwitch)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]) // user와 authLoading을 의존성에 추가

  return {
    mode: store.mode === 'NONE' ? null : store.mode,
    isFreelancer: store.mode === 'FREELANCER',
    isCompany: store.mode === 'COMPANY',
    hasFreelancer: store.hasFreelancer,
    hasCompany: store.hasCompany,
    switchMode: store.switchMode,
    loading: store.loading,
  }
}

